import { useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi } from '../personnelApi';
import {
  EDUCATION_LEVEL_LABELS,
  type EducationLevel,
  type PersonnelEducation,
} from '../personnelTypes';
import { sampleEducation } from '../utils/sampleEducation';

/**
 * Records or corrects one line of educational background — CS Form 212, section III.
 *
 * One modal for both, because the fields are identical and a separate "edit" form is a second
 * place for the same eight questions to drift.
 */
export function EditEducationModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds a line. */
  editing: PersonnelEducation | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [level, setLevel] = useState<EducationLevel>(editing?.level ?? 1);
  const [schoolName, setSchoolName] = useState(editing?.schoolName ?? '');
  const [degreeOrCourse, setDegreeOrCourse] = useState(editing?.degreeOrCourse ?? '');
  const [periodFrom, setPeriodFrom] = useState(editing?.periodFrom ?? '');
  const [periodTo, setPeriodTo] = useState(editing?.periodTo ?? '');
  const [highestLevel, setHighestLevel] = useState(editing?.highestLevelUnitsEarned ?? '');
  const [yearGraduated, setYearGraduated] = useState(
    editing?.yearGraduated ? String(editing.yearGraduated) : '',
  );
  const [honors, setHonors] = useState(editing?.honors ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    Checked here so the reader is told before submitting, and on the server so it holds for any
    caller. The client copy is the courtesy; the server's is the rule.
  */
  const endsBeforeItBegan = periodTo !== '' && periodFrom !== '' && periodTo < periodFrom;

  const year = yearGraduated.trim() === '' ? null : Number(yearGraduated);
  const yearIsNumber = year === null || (Number.isInteger(year) && year > 0);
  const graduatedBeforeStarting =
    year !== null && periodFrom !== '' && year < Number(periodFrom.slice(0, 4));

  const valid =
    schoolName.trim().length > 0 &&
    periodFrom.length > 0 &&
    !endsBeforeItBegan &&
    yearIsNumber &&
    !graduatedBeforeStarting;

  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      level,
      schoolName: schoolName.trim(),
      degreeOrCourse: degreeOrCourse.trim() || null,
      periodFrom,
      periodTo: periodTo || null,
      highestLevelUnitsEarned: highestLevel.trim() || null,
      yearGraduated: year,
      honors: honors.trim() || null,
    };

    try {
      if (editing) await personnelApi.updateEducation(personnelId, editing.id, request);
      else await personnelApi.addEducation(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'education' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleEducation();

    setLevel(sample.level);
    setSchoolName(sample.schoolName);
    setDegreeOrCourse(sample.degreeOrCourse);
    setPeriodFrom(sample.periodFrom);
    setPeriodTo(sample.periodTo);
    setYearGraduated(String(sample.yearGraduated));
    setHonors(sample.honors);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit education' : 'Add education'}
      size="2xl"
      id="education-modal"
      // Create only, never edit: on a form over existing data it would overwrite the record's
      // real values with test data, which is the one thing such a form must not do.
      autogenerate={editing === null ? { onGenerate: generate, disabled: saving } : undefined}
      confirm={{
        id: 'education-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="education-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="education-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="education-level"
            label="Level"
            required
            description="Which line of the form this is. The profile lists them earliest first."
            value={String(level)}
            onChange={(next) => setLevel(Number(next) as EducationLevel)}
            options={Object.entries(EDUCATION_LEVEL_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
            // The enum IS the reading order, so the picker must not re-sort it alphabetically.
            sort="none"
            clearable={false}
            disabled={saving}
          />

          <TextField
            id="education-school-name"
            label="School"
            required
            description="Written in full, as the form asks — never an abbreviation."
            value={schoolName}
            onChange={(e) => setSchoolName(e.target.value)}
            error={schoolName.trim() ? error?.fieldError('schoolName') : 'Enter a school name.'}
            disabled={saving}
          />

          <TextField
            id="education-degree-or-course"
            label="Degree or course"
            description="Left blank at elementary and secondary level, where the form does not ask."
            value={degreeOrCourse}
            onChange={(e) => setDegreeOrCourse(e.target.value)}
            error={error?.fieldError('degreeOrCourse')}
            disabled={saving}
          />

          <TextField
            id="education-honors"
            label="Honours"
            description="Scholarships and academic honours received."
            value={honors}
            onChange={(e) => setHonors(e.target.value)}
            error={error?.fieldError('honors')}
            disabled={saving}
          />

          <DateField
            id="education-period-from"
            label="From"
            required
            max={todayCalendarDate()}
            value={periodFrom}
            onChange={setPeriodFrom}
            error={
              periodFrom ? error?.fieldError('periodFrom') : 'Choose the day attendance began.'
            }
            disabled={saving}
          />

          <DateField
            id="education-period-to"
            label="To"
            description="Leave blank while the study is still in progress."
            max={todayCalendarDate()}
            value={periodTo}
            onChange={setPeriodTo}
            error={
              endsBeforeItBegan
                ? 'Choose a date on or after the day attendance began.'
                : error?.fieldError('periodTo')
            }
            disabled={saving}
          />

          <TextField
            id="education-year-graduated"
            label="Year graduated"
            type="number"
            min={1900}
            max={new Date().getFullYear()}
            /*
              A YEAR, not a date — it is what a diploma states and what the form collects.
              Storing 01 January would invent a precision nobody recorded.
            */
            description="Leave blank if the study did not end in a qualification."
            value={yearGraduated}
            onChange={(e) => setYearGraduated(e.target.value)}
            error={
              !yearIsNumber
                ? 'Enter a year.'
                : graduatedBeforeStarting
                  ? `Choose ${periodFrom.slice(0, 4)} or later.`
                  : error?.fieldError('yearGraduated')
            }
            disabled={saving}
          />

          <TextField
            id="education-highest-level-units-earned"
            label="Highest level / units earned"
            description="For study that did not end in a qualification — 3rd year, 36 units."
            value={highestLevel}
            onChange={(e) => setHighestLevel(e.target.value)}
            error={error?.fieldError('highestLevelUnitsEarned')}
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
}
